import { Box } from '@mui/material'
import { UpdatesIcon, useMinimalsTheme } from '@tom-ui/ui'
import { useCallback, useRef } from 'react'

interface Props {
  rotation: number
  zoom: number
  onRotate: (rotation: number) => void
  onRotationEnd: () => void
}
export const NodeRotation = ({ rotation, zoom, onRotate, onRotationEnd }: Props) => {
  const theme = useMinimalsTheme()
  const rotatingRef = useRef<{
    centerX: number
    centerY: number
    startAngle: number
    startRotation: number
  } | null>(null)

  const handleMove = useCallback(
    (e: PointerEvent) => {
      if (!rotatingRef.current) return

      const { centerX, centerY, startAngle, startRotation } = rotatingRef.current
      const currentAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI)

      onRotate(startRotation + (currentAngle - startAngle))
    },
    [onRotate],
  )

  const handleEnd = useCallback(() => {
    rotatingRef.current = null
    window.removeEventListener('pointermove', handleMove)
    window.removeEventListener('pointerup', handleEnd)

    onRotationEnd()
  }, [handleMove, onRotationEnd])

  const handleStart = useCallback(
    (e: React.PointerEvent) => {
      const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      rotatingRef.current = {
        centerX,
        centerY,
        startAngle: Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI),
        startRotation: rotation,
      }

      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleEnd)
    },
    [handleMove, handleEnd, rotation],
  )

  return (
    <Box
      className='nodrag'
      onPointerDown={e => {
        e.stopPropagation()
        handleStart(e)
      }}
      sx={{
        position: 'absolute',
        bottom: -16,
        right: -16,
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'crosshair',
        transform: `scale(${1 / zoom})`,
        transformOrigin: 'bottom right',
      }}
    >
      <UpdatesIcon />
    </Box>
  )
}
